import React from "react";
import { GroupedBulkActions } from "../../../../BulkActions/GroupedBulkActions";
import { useSimpleTableContext } from "../../../contexts/SimpleTableContext";
import { useSimpleTableMemoization } from "../hooks/useSimpleTableMemoization";
import { useSimpleTableActionContext } from "../hooks/useSimpleTableActionContext";
import type { BaseEntity } from "../../../types";

/**
 * Renders grouped bulk actions floating bar
 * Shows when items are selected and provides bulk action options
 * Consumes data directly from SimpleTableContext
 */
export function SimpleTableBulkActionsSection<TData extends BaseEntity>() {
  const context = useSimpleTableContext<TData>();
  
  const {
    state,
    config,
    paginatedData,
    callbacks,
    auth,
    propTotalItems,
    hooks,
    onBulkAction,
  } = context;

  // Memoize computed values and callbacks for bulk actions
  const memoization = useSimpleTableMemoization(
    paginatedData,
    state.selectedItems,
    state.setSelectedItems,
    config,
    onBulkAction
  );

  // Build action context for bulk actions.
  const { actionContext } = useSimpleTableActionContext(
    memoization.selectedItemsArray,
    config,
    hooks
  );

  if (!state.showFloatingBulkActions) {
    return null;
  }

  return (
    <GroupedBulkActions
      selectedItems={memoization.selectedItemsArray}
      allItems={paginatedData}
      config={config}
      editAllFieldsModal={config.editAllFieldsModal}
      onClearSelection={memoization.handleClearSelection}
      onSelectAll={memoization.handleSelectAllItems}
      onBulkEdit={callbacks.handleBulkEditField}
      appUser={auth.appUser}
      totalFilteredCount={propTotalItems ?? 0}
      onBulkEditAllFiltered={callbacks.handleBulkEditAllFiltered}
      filterCriteria={state.effectiveAdvancedFilters}
      customActions={memoization.customActions}
      // `onSelectAllPages` / `fetchAllPages` are deliberately NOT passed. Both
      // were read off the table context, nothing ever implemented them, and both
      // arrived as `undefined` — `GroupedBulkActions` treats them as optional,
      // so this is the behaviour that already shipped, now stated (#2625).
      pageSize={state.effectivePageSize}
      maxPages={1000}
      className="mb-4"
      rowActions={config.detailActions}
      actionContext={actionContext || undefined}
    />
  );
}
